import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { 
  Save,
  RefreshCw,
  Edit2,
  Globe,
  CheckCircle,
  AlertCircle,
  Search,
  X
} from 'lucide-react';
import axios from 'axios';

const PageMetadataManager = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [filteredPages, setFilteredPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [savingStates, setSavingStates] = useState({});
  const [editMode, setEditMode] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  const [bulkSaveLoading, setBulkSaveLoading] = useState(false);

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';

  // Fetch all page metadata
  const fetchPageMetadata = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/page-metadata`);
      const data = response.data || [];
      setPages(data);
      setFilteredPages(data);
      
      // Initialize edit mode state
      const initialEditMode = {};
      data.forEach(page => {
        initialEditMode[page.id] = false;
      });
      setEditMode(initialEditMode);
      
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      showNotification('Failed to load page metadata', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPageMetadata();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPages(pages);
    } else {
      const lowerSearch = searchTerm.toLowerCase();
      const filtered = pages.filter(page =>
        (page.page_name?.toLowerCase() || '').includes(lowerSearch) ||
        (page.page_key?.toLowerCase() || '').includes(lowerSearch) ||
        (page.title?.toLowerCase() || '').includes(lowerSearch)
      );
      setFilteredPages(filtered);
    }
  }, [searchTerm, pages]);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // Handle input change - Optimized to prevent typing lag
  const handleInputChange = (id, field, value) => {
    setPages(prevPages =>
      prevPages.map(page =>
        page.id === id ? { ...page, [field]: value } : page
      )
    );
  };

  // Save single page
  const savePage = async (id) => {
    const page = pages.find(p => p.id === id);
    if (!page) return;

    setSavingStates(prev => ({ ...prev, [id]: true }));

    try {
      await axios.put(`${API_BASE_URL}/api/page-metadata/${id}`, {
        page_name: page.page_name,
        title: page.title,
        description: page.description
      });
      
      showNotification(`${page.page_name} page updated successfully`, 'success');
      setEditMode(prev => ({ ...prev, [id]: false }));
      
      // Refresh to get updated timestamp
      fetchPageMetadata();
      
    } catch (error) {
      console.error('Error saving page:', error);
      showNotification(`Failed to update ${page.page_name}`, 'error');
    } finally {
      setSavingStates(prev => ({ ...prev, [id]: false }));
    }
  };

  // Save all pages (bulk update)
  const saveAllPages = async () => {
    const pagesToUpdate = pages.map(page => ({
      id: page.id,
      page_name: page.page_name,
      title: page.title,
      description: page.description
    }));

    setBulkSaveLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/page-metadata/bulk-update`, {
        pages: pagesToUpdate
      });
      
      showNotification('All pages updated successfully!', 'success');
      
      // Exit all edit modes
      const resetEditMode = {};
      pages.forEach(page => {
        resetEditMode[page.id] = false;
      });
      setEditMode(resetEditMode);
      
      // Refresh to get updated timestamps
      fetchPageMetadata();
      
    } catch (error) {
      console.error('Error in bulk update:', error);
      showNotification('Failed to update all pages', 'error');
    } finally {
      setBulkSaveLoading(false);
    }
  };

  // Cancel edit
  const cancelEdit = (id) => {
    fetchPageMetadata(); // Reload original data
    setEditMode(prev => ({ ...prev, [id]: false }));
  };

  // Character counter helper
  const getCharacterCount = (text, limit) => {
    const count = text ? String(text).length : 0;
    let colorClass = 'text-gray-400';
    if (count > limit) colorClass = 'text-red-500';
    else if (count > limit * 0.9) colorClass = 'text-yellow-500';
    return { count, colorClass };
  };

  // Validate page name
  const isValidPageName = (name) => {
    if (!name) return false;
    const regex = /^[a-zA-Z0-9\s\-\(\)&]+$/;
    return regex.test(name);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex h-screen">
        <Sidebar isOpen={sidebarOpen} />
        
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Notification */}
            {notification.show && (
              <div className={`fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg animate-in fade-in slide-in-from-top-4 ${
                notification.type === 'success' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-red-500 text-white'
              }`}>
                {notification.type === 'success' ? 
                  <CheckCircle className="w-5 h-5" /> : 
                  <AlertCircle className="w-5 h-5" />
                }
                {notification.message}
              </div>
            )}

            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                    Page Metadata Manager
                  </h1>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">
                    Manage page names, titles and descriptions for all pages (SEO optimization)
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={fetchPageMetadata}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg flex items-center gap-2 transition"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                  
                  <button
                    onClick={saveAllPages}
                    disabled={bulkSaveLoading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50"
                  >
                    {bulkSaveLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save All Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search pages by name, key, or title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </div>

            {/* Pages Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredPages.map((page) => {
                  const pageNameLimit = 100;
                  const titleLimit = 255;
                  const descLimit = 5000;
                  const pageNameCount = getCharacterCount(page.page_name, pageNameLimit);
                  const titleCount = getCharacterCount(page.title, titleLimit);
                  const descCount = getCharacterCount(page.description, descLimit);
                  const isEditing = editMode[page.id];
                  const isSaving = savingStates[page.id];
                  const isPageNameInvalid = page.page_name && !isValidPageName(page.page_name);

                  return (
                    <div
                      key={page.id}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      {/* Page Header */}
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            {isEditing ? (
                              <div className="space-y-2">
                                <label className="block text-xs font-medium text-blue-100">
                                  Page Name
                                  <span className="text-blue-200 ml-2">
                                    ({pageNameCount.count}/{pageNameLimit})
                                  </span>
                                </label>
                                <input
                                  type="text"
                                  value={page.page_name || ''}
                                  onChange={(e) => handleInputChange(page.id, 'page_name', e.target.value)}
                                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                                    pageNameCount.count > pageNameLimit || isPageNameInvalid
                                      ? 'border-red-500' 
                                      : 'border-gray-300 dark:border-gray-600'
                                  }`}
                                  placeholder="Enter page name"
                                />
                                {isPageNameInvalid && (
                                  <p className="text-xs text-red-200">
                                    Only letters, numbers, spaces, hyphens, &, and parentheses allowed
                                  </p>
                                )}
                              </div>
                            ) : (
                              <>
                                <h2 className="text-xl font-bold text-white">
                                  {page.page_name}
                                </h2>
                                <p className="text-blue-100 text-sm mt-1">
                                  Key: {page.page_key}
                                </p>
                              </>
                            )}
                          </div>
                          
                          {!isEditing && (
                            <button
                              onClick={() => setEditMode(prev => ({ ...prev, [page.id]: true }))}
                              className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white rounded-lg flex items-center gap-2 transition"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Page Content */}
                      <div className="p-6 space-y-5">
                        {/* Title Field */}
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                            Header Title
                            <span className="text-xs text-gray-500 ml-2">
                              ({titleCount.count}/{titleLimit})
                            </span>
                          </label>
                          
                          {isEditing ? (
                            <>
                              <input
                                type="text"
                                value={page.title || ''}
                                onChange={(e) => handleInputChange(page.id, 'title', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                                  titleCount.count > titleLimit 
                                    ? 'border-red-500' 
                                    : 'border-gray-300 dark:border-gray-600'
                                }`}
                                placeholder="Enter meta title"
                              />
                              {titleCount.count > titleLimit && (
                                <p className="mt-1 text-xs text-red-500">
                                  Title exceeds {titleLimit} characters
                                </p>
                              )}
                            </>
                          ) : (
                            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <p className="text-gray-900 dark:text-white">
                                {page.title || 'No title set'}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Description Field */}
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                            Page Description
                            <span className="text-xs text-gray-500 ml-2">
                              ({descCount.count}/{descLimit})
                            </span>
                          </label>
                          
                          {isEditing ? (
                            <>
                              <textarea
                                value={page.description || ''}
                                onChange={(e) => handleInputChange(page.id, 'description', e.target.value)}
                                rows={4}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                                  descCount.count > descLimit 
                                    ? 'border-red-500' 
                                    : 'border-gray-300 dark:border-gray-600'
                                }`}
                                placeholder="Enter meta description"
                              />
                              {descCount.count > descLimit && (
                                <p className="mt-1 text-xs text-red-500">
                                  Description exceeds {descLimit} characters
                                </p>
                              )}
                              <p className="mt-2 text-xs text-gray-500">
                                Recommended: 150-160 characters for optimal SEO
                              </p>
                            </>
                          ) : (
                            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                                {page.description || 'No description set'}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Last Updated */}
                        <div className="text-xs text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-200 dark:border-gray-700">
                          Last updated: {page.updated_at ? new Date(page.updated_at).toLocaleString() : 'N/A'}
                        </div>

                        {/* Action Buttons */}
                        {isEditing && (
                          <div className="flex gap-3 pt-2">
                            <button
                              onClick={() => savePage(page.id)}
                              disabled={isSaving || 
                                titleCount.count > titleLimit || 
                                descCount.count > descLimit ||
                                pageNameCount.count > pageNameLimit ||
                                isPageNameInvalid
                              }
                              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 transition disabled:opacity-50"
                            >
                              {isSaving ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save className="w-4 h-4" />
                                  Save Changes
                                </>
                              )}
                            </button>
                            
                            <button
                              onClick={() => cancelEdit(page.id)}
                              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg transition"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && filteredPages.length === 0 && (
              <div className="text-center py-12">
                <Globe className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium mb-2">No pages found</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Try adjusting your search criteria
                </p>
              </div>
            )}
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default PageMetadataManager;